<template>
  <div>
    <v-sheet class="pa-4">
      <h4 class="mb-4">Добавить настройку</h4>
      <challenge-form
        :pending="createPending"
        :error="createError"
        challenge-type="challenge"
        @submit="addSetting"
        @id-change="onFormIdChange"
      />

      <!-- Информация о найденном challenge'е -->
      <transition name="fade">
        <div v-if="challengeInfo" class="mt-4 challenge-info">
          <div class="font-weight-medium text-subtitle2 mb-1">
            {{ challengeInfo.name }}
          </div>
          <div class="text-caption text--secondary">
            {{ challengeInfo.description }}
          </div>
        </div>
      </transition>

      <!-- Загрузка информации -->
      <transition name="fade">
        <div v-if="searchLoading && formIdValue" class="mt-4">
          <v-progress-circular
            indeterminate
            size="20"
            width="2"
            color="primary"
            class="mr-2"
          ></v-progress-circular>
          <span class="text-caption text--secondary">Загружаем...</span>
        </div>
      </transition>

      <!-- Ошибка в поиске -->
      <transition name="fade">
        <div v-if="searchError" class="mt-4 error--text text-caption">
          <v-icon small class="mr-1">mdi-alert-circle</v-icon>
          {{ searchError }}
        </div>
      </transition>
    </v-sheet>
    <v-divider class="my-4"></v-divider>
    <div style="min-height: 6px">
      <v-progress-linear
        indeterminate
        color="yellow darken-2"
        :active="pending"
      ></v-progress-linear>
    </div>
    <div v-if="!!list.length">
      <v-simple-table class="mb-3">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">ID</th>
              <th class="text-left">Испытание</th>
              <th class="text-left">Описание</th>
              <th class="text-left" width="100px">Балл</th>
              <th class="text-right" width="50px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in enrichedList" :key="item.id">
              <td class="font-weight-medium">{{ item.challenge_id }}</td>
              <td>{{ item.challengeName }}</td>
              <td>
                <div class="text-caption text--secondary">
                  {{ item.challengeDescription }}
                </div>
              </td>
              <td>{{ item.score }}</td>
              <td class="text-right">
                <v-hover v-slot="{ hover }">
                  <v-btn
                    :color="hover ? 'error' : 'grey lighten-1'"
                    title="Удалить настройку"
                    icon
                    @click.stop="removeSetting(item.id)"
                  >
                    <v-icon>mdi-trash-can</v-icon>
                  </v-btn>
                </v-hover>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <app-pager
        :value="page"
        :total="total"
        :limit="limit"
        @input="getList"
      ></app-pager>
    </div>

    <div v-else-if="!pending">Список настроек пуст</div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import { apiClient } from "@/api";
import ChallengeForm from "./challenge/ChallengeForm.vue";
import { BERLOGA_AWARDS_API_URL } from "@/constants";

/**
 * @typedef {Object} ChallengeInfo
 * @property {number} id
 * @property {string} name
 * @property {string} description
 */

export default {
  name: "ChallengeSettings",
  components: {
    ChallengeForm,
  },
  props: {
    track: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      pending: false,
      page: 1,
      limit: 40,
      total: 0,
      list: [],
      error: "",
      createError: "",
      createPending: false,
      searchLoading: false,
      searchError: "",
      challengeInfo: null,
      formIdValue: undefined,
      challengesMap: new Map(),
      challengesLoading: false,
    };
  },

  computed: {
    enrichedList() {
      return this.list.map((item) => {
        const challengeInfo = this.challengesMap.get(item.challenge_id) || {};
        return {
          ...item,
          challengeName: challengeInfo.name || "-",
          challengeDescription: challengeInfo.description || "-",
        };
      });
    },
  },

  created() {
    this.getList(1);
    this.loadAllChallenges();
    this.debouncedFetchChallenge = debounce(this.fetchChallengeInfo, 600);
  },

  methods: {
    async loadAllChallenges() {
      if (this.challengesLoading) return;

      this.challengesLoading = true;
      let after;
      const limit = 50;
      let totalLoaded = 0;
      let totalCount = null;
      const allChallenges = [];

      try {
        while (totalCount === null || totalLoaded < totalCount) {
          const params = { limit };
          if (after !== undefined) {
            params.after = after;
          }

          const { data, headers } = await apiClient({
            method: "GET",
            url: "/challenges/v2",
            baseURL: BERLOGA_AWARDS_API_URL,
            params,
          });

          // Получаем общее количество из заголовка при первом запросе
          if (totalCount === null) {
            totalCount = Number.parseInt(headers["x-count"], 10) || 0;
          }

          if (Array.isArray(data) && data.length > 0) {
            allChallenges.push(...data);
            totalLoaded += data.length;

            // Если загрузили всё, выходим
            if (totalLoaded >= totalCount) {
              break;
            }

            // Используем ID последнего элемента для следующего запроса
            after = data[data.length - 1].id;
          } else {
            // Если нет данных, выходим
            break;
          }
        }

        this.challengesMap = new Map(
          allChallenges.map((challenge) => [
            challenge.id,
            {
              id: challenge.id,
              name: challenge.name || "Без названия",
              description: challenge.description || "",
            },
          ]),
        );
      } catch (error) {
        console.error("Ошибка при получении списка испытаний:", error);
      } finally {
        this.challengesLoading = false;
      }
    },

    onFormIdChange(id) {
      this.challengeInfo = null;
      this.searchError = "";

      if (!id || id < 1) {
        this.formIdValue = undefined;
        return;
      }

      this.formIdValue = id;
      this.debouncedFetchChallenge();
    },

    async fetchChallengeInfo() {
      if (!this.formIdValue) return;

      this.searchLoading = true;
      this.searchError = "";

      try {
        const { data } = await apiClient({
          method: "GET",
          url: `/challenges/v2/${this.formIdValue}`,
          baseURL: BERLOGA_AWARDS_API_URL,
        });

        if (!data || typeof data !== "object") {
          throw new Error("Некорректный формат ответа от сервера");
        }

        this.challengeInfo = {
          id: data.id,
          name: data.name || "Без названия",
          description: data.description || "Описание отсутствует",
        };
      } catch (error) {
        this.challengeInfo = null;

        if (error?.status === 404) {
          this.searchError = `Испытание с ID ${this.formIdValue} не найдено`;
        } else if (error.message) {
          this.searchError = error.message;
        } else {
          this.searchError = "Ошибка при поиске испытания";
        }

        console.error("Ошибка при поиске испытания:", error);
      } finally {
        this.searchLoading = false;
      }
    },

    async getList(page = 1) {
      const { limit } = this;
      if (this.pending) return;
      this.pending = true;
      const offset = Math.ceil(page * limit - limit);
      try {
        const { data } = await apiClient({
          method: "GET",
          url: `/tracks/${this.track.id}/settings/challenge-v2`,
          params: {
            limit,
            offset,
          },
        });
        this.list = data.results;
        this.page = page;
        if ("count" in data) {
          this.total = data.count;
        }
      } catch (error) {
        console.error("Ошибка при получении списка испытаний:", error);
        this.error = error.message || "Возникла непредвиденная ошибка";
      } finally {
        this.pending = false;
      }
    },
    async removeSetting(id) {
      this.list = this.list.filter((n) => n.id !== id);
      await apiClient({
        method: "DELETE",
        url: `/tracks/${this.track.id}/settings/challenge-v2/${id}`,
      });

      if (this.list.length > 0) {
        this.getList(Math.max(this.page));
      } else {
        this.getList(Math.max(this.page - 1, 1));
      }
      this.$toast(`Настройка для испытания ${id} удалена.`, {
        type: "success",
      });
    },
    async addSetting(setting) {
      if (this.createPending) return;
      this.createError = "";
      this.createPending = true;
      try {
        await apiClient({
          method: "POST",
          url: `/tracks/${this.track.id}/settings/challenge-v2`,
          data: {
            challenge_id: setting.id,
            score: setting.score,
          },
        });
        // Перезагружаем кэш challenges на случай если был добавлен новый
        this.loadAllChallenges();
        this.getList(1);
      } catch (error) {
        this.createError = error.message;
        console.error("Ошибка при добавлении настройки испытания:", error);
      }
      this.createPending = false;
    },
  },
};
</script>

<style scoped lang="scss">
.challenge-info {
  padding: 12px 0;
  border-left: 3px solid #4caf50;
  padding-left: 12px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
